// provisionalNodeSession.js
//
// Client session + Firestore commit/discard for create-then-Peek provisional stubs.
// Seed fingerprints live in a module Map (not Firestore).

import { deleteNode, updateNode } from 'services/nodes/NodeWriteService';
import {
  buildProvisionalSeed,
  isMeaningfulEdit,
  isProvisional,
} from 'node/provisional/provisionalNodeUtils';

export {
  buildProvisionalSeed,
  isMeaningfulEdit,
  isProvisional,
} from 'node/provisional/provisionalNodeUtils';

/** @type {Map<string, { seed: object, committed: boolean }>} */
const sessions = new Map();

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeKey(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Registers a provisional create in the client session.
 *
 * @param {object} node - Node after create (must include key).
 * @param {object} [seedSnapshot] - Optional override seed; defaults to buildProvisionalSeed(node).
 * @returns {void}
 */
export function registerProvisionalSession(node, seedSnapshot = null) {
  const key = normalizeKey(node?.key);
  if (!key) return;

  const seed = seedSnapshot && typeof seedSnapshot === 'object'
    ? seedSnapshot
    : buildProvisionalSeed(node);

  sessions.set(key, {
    seed,
    committed: false,
  });
}

/**
 * @param {string} nodeKey
 * @returns {{ seed: object, committed: boolean }|null}
 */
export function getProvisionalSession(nodeKey) {
  const key = normalizeKey(nodeKey);
  if (!key) return null;
  return sessions.get(key) || null;
}

/**
 * Clears the client session entry (after discard).
 *
 * @param {string} nodeKey
 * @returns {void}
 */
export function clearProvisionalSession(nodeKey) {
  const key = normalizeKey(nodeKey);
  if (!key) return;
  sessions.delete(key);
}

/**
 * Marks the session committed synchronously so abandon races cannot delete.
 *
 * @param {string} nodeKey
 * @returns {void}
 */
export function markProvisionalCommitted(nodeKey) {
  const key = normalizeKey(nodeKey);
  if (!key) return;
  const session = sessions.get(key);
  if (session) {
    session.committed = true;
    return;
  }
  // Commit without a prior register (e.g. expand after refresh): still block discard.
  sessions.set(key, { seed: {}, committed: true });
}

/**
 * @param {string} nodeKey
 * @param {object|null|undefined} [patch]
 * @param {object|null|undefined} [nodeBeforePatch]
 * @returns {boolean}
 */
export function shouldCommitProvisionalFromPatch(nodeKey, patch, nodeBeforePatch = null) {
  const key = normalizeKey(nodeKey);
  if (!key) return false;

  const session = sessions.get(key);
  if (session?.committed) return false;

  const looksProvisional = Boolean(session) || nodeBeforePatch?.provisional === true;
  if (!looksProvisional) return false;

  return isMeaningfulEdit(patch, session?.seed || null, nodeBeforePatch);
}

/**
 * Clears provisional on the Firestore doc. Keeps a committed session marker so
 * a stale peek seed with `provisional: true` cannot re-trigger discard.
 *
 * @param {string} nodeKey
 * @returns {Promise<boolean>}
 */
export async function commitProvisional(nodeKey) {
  const key = normalizeKey(nodeKey);
  if (!key) return false;

  markProvisionalCommitted(key);

  try {
    await updateNode(key, { provisional: false });
    return true;
  } catch (error) {
    console.error('[provisional] commit failed:', error);
    return false;
  }
}

/**
 * Synchronously arms commit for a meaningful provisional edit.
 * Call before any awaited write so peek close/back cannot discard mid-flight.
 * Prefer folding `{ provisional: false }` into that same write when possible.
 *
 * @param {string} nodeKey
 * @param {object|null|undefined} patch
 * @param {object|null|undefined} [nodeBeforePatch]
 * @returns {boolean} Whether the caller should persist `provisional: false`.
 */
export function beginProvisionalCommitFromPatch(nodeKey, patch, nodeBeforePatch = null) {
  if (!shouldCommitProvisionalFromPatch(nodeKey, patch, nodeBeforePatch)) {
    return false;
  }
  markProvisionalCommitted(nodeKey);
  return true;
}

/**
 * If the patch is meaningful for a provisional node, commit it.
 * Marks the session before the Firestore write so abandon races cannot delete.
 *
 * @param {string} nodeKey
 * @param {object|null|undefined} patch
 * @param {object|null|undefined} nodeBeforePatch
 * @returns {Promise<boolean>} Whether a commit was started/completed.
 */
export async function maybeCommitProvisionalFromPatch(nodeKey, patch, nodeBeforePatch = null) {
  if (!beginProvisionalCommitFromPatch(nodeKey, patch, nodeBeforePatch)) {
    return false;
  }
  return commitProvisional(nodeKey);
}

/**
 * Hard-deletes a provisional stub.
 *
 * @param {string} nodeKey
 * @returns {Promise<boolean>}
 */
export async function discardProvisional(nodeKey) {
  const key = normalizeKey(nodeKey);
  if (!key) return false;

  clearProvisionalSession(key);

  try {
    await deleteNode(key);
    return true;
  } catch (error) {
    console.error('[provisional] discard failed:', error);
    return false;
  }
}

/**
 * Discards when leaving a still-provisional stub (peek close / switch / back).
 *
 * @param {string} nodeKey
 * @param {object|null|undefined} [nodeHint] - Seed/live node with `provisional` when known.
 * @returns {Promise<boolean>}
 */
export async function discardIfProvisional(nodeKey, nodeHint = null) {
  const key = normalizeKey(nodeKey);
  if (!key) return false;

  const session = sessions.get(key);
  if (session?.committed) return false;

  const shouldDiscard = Boolean(session) || nodeHint?.provisional === true;
  if (!shouldDiscard) return false;

  return discardProvisional(key);
}

/**
 * Test helper — clears all sessions.
 *
 * @returns {void}
 */
export function __resetProvisionalSessionsForTests() {
  sessions.clear();
}
